import { useCallback, useEffect, useMemo, useReducer } from "react";
import Pagination from "byzantine/src/Pagination";
import {
  FilterQueryType,
  buildQueryString,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import searchFilterReducer, { SearchFilterState } from "./searchFilterReducer";

type UseSearchAndFilterOptions = {
  initialFilters: FilterQueryType;
  initialSearch?: string;
  pageSize: number;
};

const useSearchAndFilter = ({
  initialFilters,
  initialSearch = "",
  pageSize,
}: UseSearchAndFilterOptions) => {
  const [state, dispatch] = useReducer(searchFilterReducer, {
    filters: initialFilters,
    searchValue: initialSearch,
    debouncedSearch: initialSearch,
    pageNumber: 1,
  });

  const { pageNumber } = state;
  const offsetIndex = (pageNumber - 1) * pageSize;

  const queryString = useMemo(
    () => buildQueryString(state.filters, state.debouncedSearch),
    [state.filters, state.debouncedSearch]
  );

  // debounce search value
  useEffect(() => {
    const handler = setTimeout(() => {
      if (state.debouncedSearch !== state.searchValue) {
        dispatch({
          type: "SET_DEBOUNCED_SEARCH",
          debouncedSearch: state.searchValue,
        });
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [state.searchValue]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value || "";
      dispatch({ type: "SET_SEARCH_VALUE", searchValue });
    },
    []
  );

  const handleSetFilters = useCallback((newFilters: FilterQueryType) => {
    dispatch({ type: "SET_FILTERS", filters: newFilters });
  }, []);

  const handleReset = useCallback(() => {
    dispatch({
      type: "RESET",
      filters: {},
      searchValue: "",
    });
  }, []);

  const resetFromParams = useCallback(
    (filters: FilterQueryType, searchValue: string) => {
      dispatch({ type: "RESET", filters, searchValue });
    },
    []
  );

  const isFiltered =
    state.searchValue.length > 0 ||
    Object.entries(state.filters).some(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return !!value;
    });

  const createPagination = useCallback(
    (total: number) =>
      new Pagination({
        total,
        page: pageNumber,
        pageSize,
        navigatePage: (page: number) => {
          dispatch({ type: "SET_PAGE", pageNumber: page });
        },
      }),
    [pageNumber, pageSize]
  );

  const setPage = useCallback((page: number) => {
    dispatch({ type: "SET_PAGE", pageNumber: page });
  }, []);

  return {
    state,
    pageNumber,
    offsetIndex,
    queryString,
    isFiltered,
    handleSearchChange,
    handleSetFilters,
    handleReset,
    resetFromParams,
    createPagination,
    setPage,
  };
};

export type { SearchFilterState };
export default useSearchAndFilter;
